/* 
  NOTE: Cloud Functions v2 does NOT support functions.config().
  - Server-side functions in this project read email credentials from Firestore (config/email) as fallback.
  - For local development set REACT_APP_FIREBASE_* env vars or .env.local.
  - Do NOT store sensitive production secrets in client-side files.
*/

import { initializeApp } from "firebase/app";
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  OAuthProvider,
  connectAuthEmulator,
} from "firebase/auth";
import { logBootEvent } from './init/bootLogger';
import { getStorage } from "firebase/storage";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectFirestoreEmulator } from "firebase/firestore";
import { connectStorageEmulator } from "firebase/storage";

// Keep these accesses statically named so Create React App injects local
// emulator values into production builds. A dynamic process.env[name] lookup
// is not replaced reliably by the CRA compiler and silently falls back to the
// production Firebase endpoints in the browser.
const buildEnv = {
  REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
  REACT_APP_API_KEY: process.env.REACT_APP_API_KEY,
  REACT_APP_FIREBASE_AUTH_DOMAIN: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  REACT_APP_AUTH_DOMAIN: process.env.REACT_APP_AUTH_DOMAIN,
  REACT_APP_FIREBASE_DATABASE_URL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  REACT_APP_DATABASE_URL: process.env.REACT_APP_DATABASE_URL,
  REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  REACT_APP_PROJECT_ID: process.env.REACT_APP_PROJECT_ID,
  REACT_APP_FIREBASE_STORAGE_BUCKET: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  REACT_APP_STORAGE_BUCKET: process.env.REACT_APP_STORAGE_BUCKET,
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  REACT_APP_MESSAGING_SENDER_ID: process.env.REACT_APP_MESSAGING_SENDER_ID,
  REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
  REACT_APP_APP_ID: process.env.REACT_APP_APP_ID,
  REACT_APP_FIREBASE_MEASUREMENT_ID: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  REACT_APP_MEASUREMENT_ID: process.env.REACT_APP_MEASUREMENT_ID,
  REACT_APP_FIREBASE_FIRESTORE_DATABASE_ID: process.env.REACT_APP_FIREBASE_FIRESTORE_DATABASE_ID,
  REACT_APP_FIRESTORE_DATABASE_ID: process.env.REACT_APP_FIRESTORE_DATABASE_ID,
  REACT_APP_FIRESTORE_ENABLE_PERSISTENCE: process.env.REACT_APP_FIRESTORE_ENABLE_PERSISTENCE,
  REACT_APP_USE_FIREBASE_EMULATORS: process.env.REACT_APP_USE_FIREBASE_EMULATORS,
  REACT_APP_FIREBASE_EMULATOR_HOST: process.env.REACT_APP_FIREBASE_EMULATOR_HOST,
  REACT_APP_FIREBASE_AUTH_EMULATOR_PORT: process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_PORT,
  REACT_APP_FIRESTORE_EMULATOR_PORT: process.env.REACT_APP_FIRESTORE_EMULATOR_PORT,
  REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT: process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT,
};

function readEnv(name, fallback = "") {
  const value = buildEnv[name];
  return (value != null && String(value).trim() !== "") ? String(value) : fallback;
}

// Firebase config (prefer env vars; fallback to safe defaults for this repo)
// Supports both:
// - REACT_APP_FIREBASE_* (documented)
// - REACT_APP_* (legacy firebase.env)
const firebaseConfig = {
  apiKey: readEnv(
    "REACT_APP_FIREBASE_API_KEY",
    readEnv("REACT_APP_API_KEY", "AIzaSyCJTdOXoqpNW6k4v9YpPeApOy_CcNoOISY")
  ),
  authDomain: readEnv(
    "REACT_APP_FIREBASE_AUTH_DOMAIN",
    readEnv("REACT_APP_AUTH_DOMAIN", "myrobot-krinkels.firebaseapp.com")
  ),
  databaseURL: readEnv(
    "REACT_APP_FIREBASE_DATABASE_URL",
    readEnv("REACT_APP_DATABASE_URL", "https://myrobot-krinkels-default-rtdb.firebaseio.com")
  ),
  projectId: readEnv(
    "REACT_APP_FIREBASE_PROJECT_ID",
    readEnv("REACT_APP_PROJECT_ID", "myrobot-krinkels")
  ),
  storageBucket: readEnv(
    "REACT_APP_FIREBASE_STORAGE_BUCKET",
    readEnv("REACT_APP_STORAGE_BUCKET", "myrobot-krinkels.firebasestorage.app")
  ),
  messagingSenderId: readEnv(
    "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
    readEnv("REACT_APP_MESSAGING_SENDER_ID", "750492878079")
  ),
  appId: readEnv(
    "REACT_APP_FIREBASE_APP_ID",
    readEnv("REACT_APP_APP_ID", "1:750492878079:web:ff59e904882ca316cbb8ca")
  ),
  measurementId: readEnv(
    "REACT_APP_FIREBASE_MEASUREMENT_ID",
    readEnv("REACT_APP_MEASUREMENT_ID", "G-3EPKKSV53G")
  )
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const isBrowser = typeof window !== 'undefined';
const isTestEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';
const supportsIndexedDb = typeof indexedDB !== 'undefined';
const supportsLocalStorage = isBrowser && typeof window.localStorage !== 'undefined';
const canUsePersistentBrowserStorage = isBrowser && !isTestEnv && supportsIndexedDb && supportsLocalStorage;

// By default we disable persistent IndexedDB/multi-tab persistence to avoid
// primary-lease issues during startup. To explicitly enable persistent
// persistence set REACT_APP_FIRESTORE_ENABLE_PERSISTENCE=true in env.
const PERSISTENCE_ENABLED = String(readEnv('REACT_APP_FIRESTORE_ENABLE_PERSISTENCE', 'false')).toLowerCase() === 'true';

// Optional: connect to a non-default Firestore database id (multi-database)
// Example: REACT_APP_FIREBASE_FIRESTORE_DATABASE_ID=rbhdatabase
const firestoreDatabaseId = readEnv(
  "REACT_APP_FIREBASE_FIRESTORE_DATABASE_ID",
  readEnv("REACT_APP_FIRESTORE_DATABASE_ID", "rbhdatabase")
);

let firestoreInstance;
const usePersistentCache = PERSISTENCE_ENABLED && canUsePersistentBrowserStorage && typeof persistentMultipleTabManager === 'function';
try {
  const firestoreOptions = {
    ignoreUndefinedProperties: true,
    localCache: usePersistentCache
      ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
      : memoryLocalCache(),
  };

  firestoreInstance = initializeFirestore(
    app,
    firestoreOptions,
    firestoreDatabaseId || undefined
  );
} catch (persistentError) {
  // Fallback for environments where IndexedDB persistence is unavailable.
  if (!isTestEnv) {
    console.warn("Firestore persistent cache unavailable or disabled; falling back to memory cache.", persistentError);
  }
  try {
    firestoreInstance = initializeFirestore(
      app,
      {
        ignoreUndefinedProperties: true,
        localCache: memoryLocalCache(),
      },
      firestoreDatabaseId || undefined
    );
  } catch {
    firestoreInstance = firestoreDatabaseId ? getFirestore(app, firestoreDatabaseId) : getFirestore(app);
  }
}

export const db = firestoreInstance;

// V2 uses the authenticated primary Firebase app so Firestore security rules
// receive the same signed-in user. Its data paths remain V2-specific and the
// default configuration is memory-only unless persistence is explicitly
// enabled by the host application.
export const technicianMapV2Db = firestoreInstance;

// Log that Firestore has been configured for boot tracing.
try {
  logBootEvent('firestore ready');
  // expose mode
  console.info('Firestore cache mode:', usePersistentCache ? 'persistent' : 'memory-only');
} catch {
  // Boot tracing is best effort and must not block Firebase initialization.
}

let authInstance;
try {
  if (PERSISTENCE_ENABLED && canUsePersistentBrowserStorage) {
    authInstance = initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } else {
    // Prefer browserLocalPersistence for auth so that login survives a full
    // page reload. Only fall back to inMemoryPersistence if browserLocal is
    // unavailable. This avoids breaking flows that call `window.location.reload()`
    // after sign-in while still keeping Firestore memory-only if configured.
    try {
      authInstance = initializeAuth(app, {
        persistence: [
          browserLocalPersistence,
          browserSessionPersistence,
          inMemoryPersistence,
        ],
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    } catch {
      try {
        authInstance = initializeAuth(app, { persistence: browserLocalPersistence, popupRedirectResolver: browserPopupRedirectResolver });
      } catch {
        authInstance = getAuth(app);
      }
    }
  }
} catch {
  authInstance = getAuth(app);
}

export const auth = authInstance;
export const storage = getStorage(app);
export const functions = getFunctions(app);

// The emulator wiring is opt-in and only used by the local acceptance suite.
// Production builds retain their existing Firebase endpoints unless this
// explicit local flag is present at build time.
const useLocalEmulators = readEnv('REACT_APP_USE_FIREBASE_EMULATORS', 'false').toLowerCase() === 'true';
if (useLocalEmulators && isBrowser) {
  const emulatorHost = readEnv('REACT_APP_FIREBASE_EMULATOR_HOST', '127.0.0.1');
  const authPort = Number(readEnv('REACT_APP_FIREBASE_AUTH_EMULATOR_PORT', '9099'));
  const firestorePort = Number(readEnv('REACT_APP_FIRESTORE_EMULATOR_PORT', '8080'));
  const storagePort = Number(readEnv('REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT', '9199'));
  const functionsPort = Number(readEnv('REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT', '5001'));
  connectAuthEmulator(auth, `http://${emulatorHost}:${authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, emulatorHost, firestorePort);
  connectStorageEmulator(storage, emulatorHost, storagePort);
  connectFunctionsEmulator(functions, emulatorHost, functionsPort);
}
export { app };

// Helper to return the auth client at runtime. Some modules import auth
// early during module evaluation; using this getter avoids timing issues.
export function getAuthClient() {
  try {
    return authInstance || getAuth(app);
  } catch {
    return null;
  }
}

// Analytics (optional): Firebase Analytics only works in supported browser environments.
// This stays lazy/dynamic so Jest, SSR-like environments, and older browsers don't crash on import.
let analyticsPromise;

export function getAnalyticsIfSupported() {
  if (analyticsPromise) return analyticsPromise;

  analyticsPromise = (async () => {
    if (typeof window === 'undefined') return null;
    if (!firebaseConfig.measurementId) return null;

    try {
      const mod = await import('firebase/analytics');
      const supported = await mod.isSupported().catch(() => false);
      if (!supported) return null;
      return mod.getAnalytics(app);
    } catch {
      return null;
    }
  })();

  return analyticsPromise;
}

// Firebase is now properly configured
export const isFirebaseConfigured = Boolean(firebaseConfig && firebaseConfig.projectId);

// OIDC helper for Firebase (OpenID Connect providers configured in Firebase Console)
// Default provider id can be overridden by passing a different providerId
export const DEFAULT_OIDC_PROVIDER_ID = 'azure_krinkels';

export function createOIDCProvider(providerId = DEFAULT_OIDC_PROVIDER_ID) {
  const oidcProviderId = `oidc.${providerId}`; // matches Firebase Console provider id
  const provider = new OAuthProvider(oidcProviderId);
  provider.addScope('openid');
  provider.addScope('profile');
  provider.addScope('email');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}
